import { useState, useEffect } from 'react';
import { 
  Search, Filter, MapPin, Truck, Clock, Package, 
  Eye, Bell, CheckCircle, AlertTriangle, FileText, Loader2,
  Route, X, GripVertical, Plus, Trash2, ArrowUp, ArrowDown,
} from 'lucide-react';
import { Shipment, SHIPMENT_STATUS_CONFIG, ShipmentStatus } from '../types/shipment';
import { shipmentService } from '../services/shipmentService';
import { shipmentServiceReal } from '../services/shipmentServiceReal';

export function ShipmentMonitoring() {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [selectedShipmentRoute, setSelectedShipmentRoute] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ShipmentStatus | 'all'>('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRouteChangeModal, setShowRouteChangeModal] = useState(false);
  const [routeChangeReason, setRouteChangeReason] = useState('');
  const [routeChangeSubmitting, setRouteChangeSubmitting] = useState(false);
  const [routeChangeLoading, setRouteChangeLoading] = useState(false);
  const [routePoints, setRoutePoints] = useState<Array<any & { customer_name?: string }>>([]);
  const [draggedPointIndex, setDraggedPointIndex] = useState<number | null>(null);
  const [showAddPointForm, setShowAddPointForm] = useState(false);
  const [newPointForm, setNewPointForm] = useState({
    location_name: '',
    city: '',
    district: '',
    address: '',
    operation_type: 'loading' as 'loading' | 'unloading',
    latitude: '',
    longitude: '',
  });

  useEffect(() => {
    loadShipments();
    
    const interval = setInterval(loadShipments, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadShipments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await shipmentService.getAll();
      const data = response.data || response;
      if (Array.isArray(data)) {
        const mapped = data
          .filter((s: any) => s.status !== 'delivery-completed' && s.current_stage !== 10)
          .map((s: any) => {
            const currentStatus = s.status || s.current_status || 'pending';
            const statusHistory = Array.isArray(s.status_history || s.statusHistory) && (s.status_history || s.statusHistory).length > 0
              ? (s.status_history || s.statusHistory)
              : [{
                  id: `${s.id || s.shipment_id}-current`,
                  timestamp: s.updated_at || s.created_at || new Date().toISOString(),
                  status: currentStatus,
                  notes: s.notes || '',
                }];

            return {
              id: s.id?.toString() || s.shipment_id?.toString() || '',
              sevkKodu: s.shipment_no || s.sevk_kodu || s.id,
              driverId: s.driver_id || '',
              driverName: s.driver_name || s.full_name || '',
              vehiclePlate: s.vehicle_plate || '',
              currentStatus,
              loadingCity: s.loading_city || s.origin_city || '',
              loadingPoint: s.loading_point || s.loading_adress || '',
              loadingAddress: s.loading_address || '',
              deliveryCity: s.delivery_city || s.destination_city || '',
              deliveryPoint: s.delivery_point || s.delivery_adress || '',
              deliveryAddress: s.delivery_address || '',
              cargoType: s.cargo_type || '',
              cargoWeight: s.cargo_weight || 0,
              cargoVolume: s.cargo_volume || 0,
              plannedLoadingTime: s.planned_loading_date || s.loading_date || new Date().toISOString(),
              plannedDeliveryTime: s.planned_delivery_date || s.delivery_date || new Date().toISOString(),
              statusHistory,
              rampAssignment: s.ramp_assignment || s.rampAssignment || null,
              documents: s.documents || [],
            };
          });
        setShipments(mapped);
      }
    } catch (err: any) {
      console.error('Failed to load shipments:', err);
      setError('Sevkiyatlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const filteredShipments = shipments.filter(shipment => {
    const matchesSearch = 
      shipment.sevkKodu.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.driverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.vehiclePlate.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || shipment.currentStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusCounts = () => {
    const counts: Record<string, number> = {
      total: shipments.length,
      'heading-to-loading': 0,
      'loading-in-progress': 0,
      'heading-to-delivery': 0,
      'unloading-in-progress': 0,
    };

    shipments.forEach(s => {
      if (counts[s.currentStatus] !== undefined) {
        counts[s.currentStatus]++;
      }
    });

    return counts;
  };

  const viewDetails = async (shipment: Shipment) => {
    setSelectedShipment(shipment);
    setSelectedShipmentRoute([]);
    setShowDetailModal(true);

    try {
      const response: any = await shipmentServiceReal.getRoutes(shipment.id);
      if (response?.success && response?.data) {
        setSelectedShipmentRoute(response.data);
      }
    } catch {
      setSelectedShipmentRoute([]);
    }
  };

  const openRouteChangeModal = async () => {
    if (!selectedShipment) return;
    setRouteChangeReason('');
    setShowAddPointForm(false);
    setNewPointForm({
      location_name: '',
      city: '',
      district: '',
      address: '',
      operation_type: 'loading',
      latitude: '',
      longitude: '',
    });

    setRouteChangeLoading(true);
    try {
      const response: any = await shipmentServiceReal.getRoutes(selectedShipment.id);
      if (response?.success && response?.data) {
        setRoutePoints(response.data);
      } else {
        setRoutePoints([]);
      }
    } catch {
      setRoutePoints([]);
    } finally {
      setRouteChangeLoading(false);
    }
    setShowRouteChangeModal(true);
  };

  const moveRoutePoint = (fromIndex: number, toIndex: number) => {
    const updated = [...routePoints];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setRoutePoints(updated.map((point, i) => ({ ...point, stop_order: i + 1 })));
  };

  const removeRoutePoint = (index: number) => {
    setRoutePoints(routePoints.filter((_, i) => i !== index));
  };

  const addNewPoint = () => {
    if (!newPointForm.location_name.trim() && !newPointForm.city.trim()) {
      alert('Nokta adı veya il bilgisi zorunludur');
      return;
    }
    const newPoint = {
      id: Date.now(),
      location_name: newPointForm.location_name,
      city: newPointForm.city,
      district: newPointForm.district,
      address: newPointForm.address,
      operation_type: newPointForm.operation_type,
      latitude: newPointForm.latitude ? parseFloat(newPointForm.latitude) : null,
      longitude: newPointForm.longitude ? parseFloat(newPointForm.longitude) : null,
      stop_order: routePoints.length + 1,
      is_completed: 0,
      customer_name: null,
    };
    setRoutePoints([...routePoints, newPoint]);
    setNewPointForm({
      location_name: '',
      city: '',
      district: '',
      address: '',
      operation_type: 'loading',
      latitude: '',
      longitude: '',
    });
    setShowAddPointForm(false);
  };

  const submitRouteChange = async () => {
    if (!routeChangeReason.trim()) {
      alert('Rota değişikliği için gerekçe zorunludur');
      return;
    }
    if (!selectedShipment) return;
    if (routePoints.length === 0) {
      alert('Rota en az bir nokta içermelidir');
      return;
    }

    setRouteChangeSubmitting(true);
    try {
      const payload = routePoints.map((p) => ({
        customer_id: p.customer_id || null,
        location_name: p.location_name || null,
        address: p.address || null,
        city: p.city || null,
        district: p.district || null,
        latitude: p.latitude,
        longitude: p.longitude,
        stop_order: p.stop_order,
        operation_type: p.operation_type,
      }));

      await shipmentServiceReal.changeRoute(selectedShipment.id, {
        reason: routeChangeReason.trim(),
        routePoints: payload,
      });

      alert('Rota başarıyla değiştirildi');
      setShowRouteChangeModal(false);
      setShowDetailModal(false);
      loadShipments();
    } catch (error: any) {
      alert(error?.response?.data?.message || 'Rota değiştirilirken hata oluştu');
    } finally {
      setRouteChangeSubmitting(false);
    }
  };

  const counts = getStatusCounts();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sevkiyat İzleme</h1>
        <p className="text-gray-600 mt-1">Tüm aktif sevkiyatları gerçek zamanlı takip edin</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Toplam Aktif</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{counts.total}</p>
            </div>
            <Truck className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Yüklemeye Gidiyor</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">{counts['heading-to-loading']}</p>
            </div>
            <MapPin className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Yükleniyor</p>
              <p className="text-2xl font-bold text-orange-900 mt-1">{counts['loading-in-progress']}</p>
            </div>
            <Package className="w-8 h-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Teslimata Gidiyor</p>
              <p className="text-2xl font-bold text-green-900 mt-1">{counts['heading-to-delivery']}</p>
            </div>
            <MapPin className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Boşaltılıyor</p>
              <p className="text-2xl font-bold text-purple-900 mt-1">{counts['unloading-in-progress']}</p>
            </div>
            <Package className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Sevk kodu, sürücü veya plaka ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ShipmentStatus | 'all')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="heading-to-loading">Yükleme Noktasına Gidiyor</option>
              <option value="arrived-at-loading">Yükleme Noktasına Ulaştı</option>
              <option value="waiting-for-loading">Yükleme Bekliyor</option>
              <option value="loading-in-progress">Yükleme Yapılıyor</option>
              <option value="loading-completed">Yükleme Tamamlandı</option>
              <option value="heading-to-delivery">Teslimat Noktasına Gidiyor</option>
              <option value="arrived-at-delivery">Teslimat Noktasına Vardı</option>
              <option value="waiting-for-unloading">Boşaltma Bekliyor</option>
              <option value="unloading-in-progress">Boşaltma Gerçekleşiyor</option>
              <option value="unloading-completed">Boşaltma Tamamlandı</option>
            </select>
          </div>
        </div>
      </div>

      {/* Shipments Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sevk Kodu</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sürücü</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Araç</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rota</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Durum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Son Güncelleme</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredShipments.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Sevkiyat bulunamadı
                  </td>
                </tr>
              ) : (
                filteredShipments.map(shipment => {
                  const config = SHIPMENT_STATUS_CONFIG[shipment.currentStatus] || {
                    label: 'Bilinmiyor',
                    color: '#6b7280',
                    bgColor: '#f3f4f6',
                    description: 'Durum bilinmiyor'
                  };
                  const lastUpdate = shipment.statusHistory?.[shipment.statusHistory.length - 1] || { timestamp: new Date().toISOString() };
                  
                  return (
                    <tr key={shipment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-semibold text-gray-900">{shipment.sevkKodu}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                            {shipment.driverName.charAt(0)}
                          </div>
                          <span className="text-sm text-gray-900">{shipment.driverName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900">{shipment.vehiclePlate}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <div className="text-gray-900 font-medium">{shipment.loadingCity}</div>
                          <div className="text-gray-500">↓</div>
                          <div className="text-gray-900 font-medium">{shipment.deliveryCity}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span 
                          className="inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                          style={{ backgroundColor: config.bgColor, color: config.color }}
                        >
                          {config.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600">
                          {new Date(lastUpdate.timestamp).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => viewDetails(shipment)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          Detay
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedShipment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                Sevkiyat Detayı: {selectedShipment.sevkKodu}
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <span className="text-2xl text-gray-400">&times;</span>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Current Status */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Mevcut Durum</h4>
                <div 
                  className="p-4 rounded-lg"
                  style={{ 
                    backgroundColor: (SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus] || {
                      bgColor: '#f3f4f6'
                    }).bgColor
                  }}
                >
                  <p 
                    className="font-semibold text-lg"
                    style={{ 
                      color: (SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus] || {
                        color: '#6b7280'
                      }).color
                    }}
                  >
                    {(SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus] || {
                      label: 'Bilinmiyor'
                    }).label}
                  </p>
                  <p className="text-sm mt-1" style={{ 
                    color: (SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus] || {
                      color: '#6b7280'
                    }).color
                  }}>
                    {(SHIPMENT_STATUS_CONFIG[selectedShipment.currentStatus] || {
                      description: 'Durum bilinmiyor'
                    }).description}
                  </p>
                </div>
              </div>

              {/* Route & Cargo Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Rota Bilgileri</h4>
                  <div className="space-y-3">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-xs text-blue-600 font-medium">YÜKLEME</p>
                      <p className="font-semibold text-blue-900 mt-1">{selectedShipment.loadingPoint}</p>
                      <p className="text-sm text-blue-700">{selectedShipment.loadingAddress}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-xs text-green-600 font-medium">TESLİMAT</p>
                      <p className="font-semibold text-green-900 mt-1">{selectedShipment.deliveryPoint}</p>
                      <p className="text-sm text-green-700">{selectedShipment.deliveryAddress}</p>
                    </div>
                  </div>
                  <button
                    onClick={openRouteChangeModal}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors text-sm font-medium"
                  >
                    <Route className="w-4 h-4" />
                    Rotayı Değiştir
                  </button>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Yük & Araç</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sürücü:</span>
                      <span className="font-semibold text-gray-900">{selectedShipment.driverName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Plaka:</span>
                      <span className="font-semibold text-gray-900">{selectedShipment.vehiclePlate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Yük Tipi:</span>
                      <span className="font-semibold text-gray-900">{selectedShipment.cargoType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ağırlık:</span>
                      <span className="font-semibold text-gray-900">{selectedShipment.cargoWeight.toLocaleString()} kg</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Hacim:</span>
                      <span className="font-semibold text-gray-900">{selectedShipment.cargoVolume} m³</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rampa Assignment */}
              {selectedShipment.rampAssignment && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Rampa Ataması</h4>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium text-purple-900">{selectedShipment.rampAssignment.rampName}</p>
                        <p className="text-sm text-purple-700">
                          Atanan: {new Date(selectedShipment.rampAssignment.assignedAt).toLocaleString('tr-TR')}
                        </p>
                  </div>
                </div>
              </div>

              {/* Route Timeline Preview */}
              {selectedShipmentRoute.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Rota Ön İzleme</h4>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="space-y-0">
                      {selectedShipmentRoute.map((point, index) => {
                        const isCompleted = !!point.is_completed;
                        const isLast = index === selectedShipmentRoute.length - 1;
                        return (
                          <div key={point.id} className="flex gap-3">
                            <div className="flex flex-col items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isCompleted
                                  ? 'bg-green-500 text-white'
                                  : 'bg-white border-2 border-gray-300 text-gray-500'
                              }`}>
                                {isCompleted ? (
                                  <CheckCircle className="w-4 h-4" />
                                ) : (
                                  <span className="text-xs font-bold">{point.stop_order}</span>
                                )}
                              </div>
                              {!isLast && (
                                <div className={`w-0.5 h-10 ${isCompleted ? 'bg-green-300' : 'bg-gray-200'}`} />
                              )}
                            </div>

                            <div className={`flex-1 pb-4 ${isCompleted ? 'opacity-60' : ''}`}>
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wide ${
                                  point.operation_type === 'loading'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-green-100 text-green-700'
                                }`}>
                                  {point.operation_type === 'loading' ? 'Yükleme' : 'Boşaltma'}
                                </span>
                                {isCompleted && (
                                  <span className="text-[10px] text-green-600 font-medium">Tamamlandı</span>
                                )}
                              </div>
                              <p className="text-sm font-medium text-gray-900">
                                {point.location_name || point.customer_name || '—'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {[point.city, point.district, point.address].filter(Boolean).join(' / ') || '—'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between text-xs text-gray-500">
                      <span>Toplam durak: {selectedShipmentRoute.length}</span>
                      <span>Tamamlanan: {selectedShipmentRoute.filter(p => p.is_completed).length}</span>
                      <span>Kalan: {selectedShipmentRoute.filter(p => !p.is_completed).length}</span>
                    </div>
                  </div>
                </div>
              )}
                </div>
              )}

              {/* Documents */}
              {selectedShipment.documents.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Yüklenen Evraklar</h4>
                  <div className="space-y-2">
                    {selectedShipment.documents.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-gray-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.fileName}</p>
                            <p className="text-xs text-gray-600">
                              {doc.type === 'loading' ? 'Yükleme Evrakı' : 'Teslimat Evrakı'}
                            </p>
                          </div>
                        </div>
                        <a 
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Görüntüle
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status History */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Durum Geçmişi</h4>
                <div className="space-y-3">
                  {selectedShipment.statusHistory.map((update, index) => {
                    const config = SHIPMENT_STATUS_CONFIG[update.status] || {
                      label: 'Bilinmiyor',
                      color: '#6b7280',
                      bgColor: '#f3f4f6',
                      description: 'Durum bilinmiyor'
                    };
                    return (
                      <div key={update.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: config.bgColor }}
                          >
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: config.color }}
                            />
                          </div>
                          {index < selectedShipment.statusHistory.length - 1 && (
                            <div className="w-0.5 h-full bg-gray-200 my-1" />
                          )}
                        </div>
                        <div className="flex-1 pb-3">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-gray-900 text-sm">{config.label}</p>
                            <p className="text-xs text-gray-500">
                              {update.timestamp ? new Date(update.timestamp).toLocaleString('tr-TR') : '-'}
                            </p>
                          </div>
                          {update.notes && (
                            <p className="text-sm text-gray-600 mt-1">{update.notes}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Route Change Modal */}
      {showRouteChangeModal && selectedShipment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Route className="w-5 h-5 text-amber-600" />
                <h3 className="text-lg font-bold text-gray-900">Rota Değiştir</h3>
              </div>
              <button
                onClick={() => setShowRouteChangeModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Değişiklik Gerekçesi <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={routeChangeReason}
                  onChange={(e) => setRouteChangeReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  rows={3}
                  placeholder="Rotayı neden değiştirmek istediğinizi açıklayınız..."
                />
              </div>

              {/* Route Points List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Rota Noktaları (Sıralamayı sürükleyerek değiştirin)</h4>
                  <button
                    onClick={() => setShowAddPointForm(true)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-xs font-medium"
                  >
                    <Plus className="w-3 h-3" /> Nokta Ekle
                  </button>
                </div>

                {routeChangeLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : routePoints.length === 0 ? (
                  <div className="text-center py-6 text-gray-400 text-sm">Henüz rota noktası bulunmuyor</div>
                ) : (
                  <div className="space-y-2">
                    {routePoints.map((point, index) => (
                      <div
                        key={point.id || index}
                        draggable
                        onDragStart={() => setDraggedPointIndex(index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (draggedPointIndex !== null && draggedPointIndex !== index) {
                            moveRoutePoint(draggedPointIndex, index);
                          }
                          setDraggedPointIndex(null);
                        }}
                        onDragEnd={() => setDraggedPointIndex(null)}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                          draggedPointIndex === index ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'
                        } ${point.is_completed ? 'opacity-60' : ''}`}
                      >
                        <div className="cursor-move pt-1 text-gray-400 hover:text-gray-600">
                          <GripVertical className="w-4 h-4" />
                        </div>

                        <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white bg-amber-500">
                          {point.stop_order || index + 1}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                              point.operation_type === 'loading'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {point.operation_type === 'loading' ? 'Yükleme' : 'Boşaltma'}
                            </span>
                            {point.is_completed ? (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Tamamlandı
                              </span>
                            ) : null}
                          </div>
                          <p className="text-sm font-medium text-gray-900 mt-1">
                            {point.location_name || point.customer_name || '-'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {[point.city, point.district, point.address].filter(Boolean).join(' / ') || 'Adres bilgisi yok'}
                          </p>
                          {point.customer_name && (
                            <p className="text-xs text-gray-400 mt-0.5">Müşteri: {point.customer_name}</p>
                          )}
                        </div>

                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => moveRoutePoint(index, Math.max(0, index - 1))}
                            disabled={index === 0}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ArrowUp className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => moveRoutePoint(index, Math.min(routePoints.length - 1, index + 1))}
                            disabled={index === routePoints.length - 1}
                            className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                          >
                            <ArrowDown className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => removeRoutePoint(index)}
                            className="p-1 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Point Form */}
              {showAddPointForm && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  <h5 className="text-sm font-semibold text-gray-700">Yeni Rota Noktası</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={newPointForm.location_name}
                      onChange={(e) => setNewPointForm({ ...newPointForm, location_name: e.target.value })}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Nokta adı / Müşteri"
                    />
                    <select
                      value={newPointForm.operation_type}
                      onChange={(e) => setNewPointForm({ ...newPointForm, operation_type: e.target.value as 'loading' | 'unloading' })}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="loading">Yükleme</option>
                      <option value="unloading">Boşaltma</option>
                    </select>
                    <input
                      type="text"
                      value={newPointForm.city}
                      onChange={(e) => setNewPointForm({ ...newPointForm, city: e.target.value })}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="İl"
                    />
                    <input
                      type="text"
                      value={newPointForm.district}
                      onChange={(e) => setNewPointForm({ ...newPointForm, district: e.target.value })}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="İlçe"
                    />
                    <input
                      type="text"
                      value={newPointForm.address}
                      onChange={(e) => setNewPointForm({ ...newPointForm, address: e.target.value })}
                      className="col-span-2 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Adres"
                    />
                    <input
                      type="text"
                      value={newPointForm.latitude}
                      onChange={(e) => setNewPointForm({ ...newPointForm, latitude: e.target.value })}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Enlem"
                    />
                    <input
                      type="text"
                      value={newPointForm.longitude}
                      onChange={(e) => setNewPointForm({ ...newPointForm, longitude: e.target.value })}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Boylam"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={addNewPoint}
                      className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
                    >
                      Ekle
                    </button>
                    <button
                      onClick={() => setShowAddPointForm(false)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                    >
                      Vazgeç
                    </button>
                  </div>
                </div>
              )}

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Dikkat!</p>
                    <p>Rota sıralaması ve noktaları güncellenecektir. Değişiklikler rota geçmişinde saklanacaktır.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowRouteChangeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={submitRouteChange}
                disabled={routeChangeSubmitting || !routeChangeReason.trim() || routePoints.length === 0}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {routeChangeSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Route className="w-4 h-4" />
                    Rotayı Kaydet ({routePoints.length} nokta)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
